<template lang="pug">
#event-reply-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span イベント代理参加申込み情報登録
    .box-body.register-body
      FormInputSelect(
        key-name="利用者グループ"
        name="resident-group"
        v-model="residentGroupId"
        :options="residentGroupOptions"
        :styles="{ 'max-width': '470px' }")
      FormInputSelect(
        key-name="利用者"
        name="resident"
        v-model="residentId"
        :options="residentOptions"
        :styles="{ 'max-width': '470px' }")
      FormInputSelect(
        key-name="家族ユーザー"
        required
        name="family"
        v-model="residentsSortKey"
        :options="familyOptions"
        :validation-rules="{ required: true }"
        :styles="{ 'max-width': '470px' }")

      .attendees
        .attendees-title 参加者情報
        .attendees-note
          span イベント参加者を一名ずつ入力してください。
          br
          span 追加ボタンを押すと入力欄を増やすことが出来ます。
        .attendees-members
          .d-flex.flex-row.justify-content-start.mb-2(v-for="(attendee, index) in attendees")
            .attendee-name.mr-2
              b-form-input(v-model="attendee.name " placeholder="参加者名" :name="'attendee-name-' + index" :maxlength="32"
                v-validate="{ required: true, max: 32 }" :data-vv-as="(index + 1) + '人目の参加者名'")
            .attendee-type.mr-2
              b-form-select.attendee-type-select(v-model="attendee.attendanceType" :options="attendanceTypeOptions" :name="'attendee-type-' + index"
                v-validate="{ required: true }" :data-vv-as="(index + 1) + '人目の参加種別'")
            .attendee-delete
              button.btn.attendee-delete-btn(@click="onAttendeeDelete(index)" type="button") 削除
        .add-attendee
          .d-flex.flex-row.justify-content-end
            button.btn.add-attendee-btn(@click="onAddAttendee()" type="button") 追加

        .error-messages-contents(v-for="(attendee, index) in attendees")
          .error-messages(v-for="error in errors.collect('attendee-name-' + index)")
            .error-message {{ error }}
          .error-messages(v-for="error in errors.collect('attendee-type-' + index)")
            .error-message {{ error }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmRegisterModal" type="button") 登録
        button.btn.cancel-btn(@click="gotoEventList" type="button") キャンセル

  OkCancelModal(v-model="showRegisterModal" title="イベント代理参加申込み情報を登録しますか？" @ok="registerEventReply")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoEventList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { EventListState } from "@/store/NeedRefreshes";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import EventRepliesService, { Attendees, AttendType, EventRepliesPostModel } from "@/commons/services/EventReplies";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import ResidentsService, { ResidentGetModel } from "@/commons/services/Residents";
import FamiliesService, { FamilyGetModel } from "@/commons/services/Families";
import EventsService, { EventGetModel, EventHoldType } from "@/commons/services/Events";
import SystemError from "@/commons/models/SystemError";

import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";

/**
 * イベント返信登録画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    ValidationAlert,
    OkCancelModal,
    OkModal,
    FormInputSelect
  },
  $_veeValidate: { validator: "new" }
})
export default class EventReplyRegister extends Vue {
  /** イベント返信サービス */
  @Inject("EventRepliesService") eventRepliesService!: EventRepliesService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 家族サービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;
  /** イベントサービス */
  @Inject("EventsService") eventsService!: EventsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 利用者グループID */
  residentGroupId: number = 0;
  /** 利用者グループセレクトボックス用項目 */
  residentGroupOptions: Array<{ value: number | string; label: string }> = [];
  /** 利用者グループMAP。key：利用者グループID、value：利用者グループ情報 */
  residentGroupMap: Map<number, ResidentGroupsGetModel> = new Map<number, ResidentGroupsGetModel>();
  /** セレクトボックス用項目で使用する利用者 */
  residents: Array<ResidentGetModel> = [];
  /** 利用者MAP。key：利用者ID、value：利用者情報 */
  residentMap: Map<number, ResidentGetModel> = new Map<number, ResidentGetModel>();
  /** 利用者ID */
  residentId: any | number = "";
  /** 利用者セレクトボックス用項目 */
  residentOptions: Array<{ value: number | string; label: string }> = [];
  /** 家族を特定するための利用者ソートキー */
  residentsSortKey: string = "";
  /** 家族選択肢 */
  familyOptions: Array<{ value: number | string; label: string }> = [];
  /** セレクトボックス用項目で使用する家族 */
  families: Array<FamilyGetModel> = [];
  /** 家族MAP。key：利用者ID+家族ID、value：家族情報 */
  familyMap: Map<string, FamilyGetModel> = new Map<string, FamilyGetModel>();

  /** イベント参加申し込み情報 */
  attendees: Array<{ name: string; attendanceType: AttendType | undefined }> = [{ name: "", attendanceType: undefined }];
  /** イベント参加種別の選択肢 */
  attendanceTypeOptions: Array<{ value: AttendType; text: string }> = [];
  /** 画面表示時に取得したイベント */
  event: EventGetModel | null = null;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### 利用者グループIDの値更新を監視する
   *
   * - 利用者IDをリセットする
   * - 利用者のセレクトボックス選択項目を更新する
   */
  @Watch("residentGroupId")
  public changeResidentGroupId(groupId: number): void {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const residentOptions: Array<{ value: number; label: string }> = this.createResidentOptions(this.residents, groupId);
    this.residentOptions = residentOptions;

    // 利用者IDが0の場合はWatchで呼び出すメソッドが呼び出されないので自分で呼ぶ
    if (this.residentId == 0) {
      this.changeResidentId(0);
    } else {
      this.residentId = 0;
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### 利用者IDの値更新を監視する
   *
   * - 家族IDをリセットする
   * - 家族のセレクトボックス選択項目を更新する
   */
  @Watch("residentId")
  public changeResidentId(residentId: any | number): void {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    let residentIds: number[] = [];
    if (residentId != 0) {
      residentIds.push(residentId);
    } else {
      for (const resident of this.residents) {
        if (resident.groupId == this.residentGroupId || this.residentGroupId == 0) {
          residentIds.push(resident.id);
        }
      }
    }

    const familyOptions: Array<{ value: string; label: string }> = this.createFamilyOptions(this.families, residentIds);
    this.residentsSortKey = "";
    this.familyOptions = familyOptions;

    this.isBusy = false;

    return;
  }

  /**
   * vue lifecycle created
   */
  public created() {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 返信元のイベントから参加方法の選択肢を作成する
   * - セレクトボックスの選択肢となる以下を取得する
   *   - 利用者
   *   - 利用者グループ
   * - 画面表示情報のために家族情報を取得する
   *   - 家族情報には利用者情報も含まれるので、家族情報を取得するのみ
   * - 家族情報から画面表示情報を作成する
   *   - 利用者名（部屋名）
   *   - 家族名（続柄）
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const parentEventsSortKey: string = this.$route.params.id;
    const eventId: number = Number(parentEventsSortKey.split("_")[0]);
    const event: EventGetModel = await this.getEvent(eventId);
    this.event = event;

    const attendanceTypeOptions: Array<{ value: AttendType; text: string }> = this.createAttendanceTypeOptions(event.holdType);
    this.attendanceTypeOptions = attendanceTypeOptions;

    const residents: Array<ResidentGetModel> = await this.getResidents();
    const residentMap: Map<number, ResidentGetModel> = this.createResidentMap(residents);
    const residentOptions: Array<{ value: number; label: string }> = this.createResidentOptions(residents, this.residentGroupId);
    this.residents = residents;
    this.residentMap = residentMap;
    this.residentId = 0;
    this.residentOptions = residentOptions;

    const residentGroups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const residentGroupMap: Map<number, ResidentGroupsGetModel> = this.createResidentGroupMap(residentGroups);
    const residentGroupOptions: Array<{ value: number; label: string }> = this.createResidentGroupOptions(residentGroups, residents);
    this.residentGroupMap = residentGroupMap;
    this.residentGroupId = 0;
    this.residentGroupOptions = residentGroupOptions;

    const families: Array<FamilyGetModel> = await this.getFamilies();
    const familyMap: Map<string, FamilyGetModel> = this.createFamilyMap(families);
    const familyOptions: Array<{ value: string; label: string }> = this.createFamilyOptions(families, [this.residentId]);
    this.familyMap = familyMap;
    this.families = families;
    this.residentsSortKey = "0";
    this.familyOptions = familyOptions;

    this.isBusy = false;

    return;
  }

  /**
   * ### 利用者取得
   */
  public async getResidents(): Promise<Array<ResidentGetModel>> {
    let response: Array<ResidentGetModel> = [];

    try {
      response = await this.residentsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        const count: number = await this.residentsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params: { startIndex: number; endIndex: number } = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount
          };
          response = response.concat(await this.residentsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 利用者MAPを作成する
   *
   * - key：利用者ID
   * - value：利用者情報
   */
  public createResidentMap(residents: Array<ResidentGetModel>): Map<number, ResidentGetModel> {
    let map: Map<number, ResidentGetModel> = new Map<number, ResidentGetModel>();

    for (const resident of residents) {
      map.set(resident.id, resident);
    }

    return map;
  }

  /**
   * ### 利用者セレクトボックス用項目を作成する
   *
   * - 渡されたグループIDを持つ利用者のみを項目にする
   * - 渡されたグループIDが0(全て)の場合は全利用者を項目にする
   * - 選択肢の表示項目名は「全て」と「利用者名（利用者部屋名）」
   */
  public createResidentOptions(residents: Array<ResidentGetModel>, groupId: number): Array<{ value: number; label: string }> {
    let options: Array<{ value: number; label: string }> = [];

    options.push({
      value: 0,
      label: `全て`
    });

    for (const resident of residents) {
      if (resident.groupId == groupId || groupId == 0) {
        options.push({
          value: resident.id,
          label: `${resident.name}（${resident.room}）`
        });
      }
    }

    return options;
  }

  /**
   * ### 利用者グループ取得
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループMAPを作成する
   *
   * - key：利用者グループID
   * - value：利用者グループ情報
   */
  public createResidentGroupMap(residentGroups: Array<ResidentGroupsGetModel>): Map<number, ResidentGroupsGetModel> {
    let map: Map<number, ResidentGroupsGetModel> = new Map<number, ResidentGroupsGetModel>();

    for (const residentGroup of residentGroups) {
      map.set(residentGroup.id, residentGroup);
    }

    return map;
  }

  /**
   * ### 利用者グループセレクトボックス用項目を作成する
   *
   * - 利用者グループに「全て」を追加する
   * - 全て以外は渡された利用者グループで項目を作成する
   *   - ユーザーの権限を見て、閲覧できない権限の利用者グループは選択肢にしない
   *   - 利用者が一人もいない利用者グループは選択肢にしない
   */
  public createResidentGroupOptions(residentGroups: Array<ResidentGroupsGetModel>, residents: Array<ResidentGetModel>): Array<{ value: number; label: string }> {
    const options: Array<{ value: number; label: string }> = [];

    options.push({
      value: 0,
      label: `全て`
    });

    const canUseResidentGroupMap: Map<number, number> = new Map<number, number>();
    for (const resident of residents) {
      canUseResidentGroupMap.set(resident.groupId, resident.groupId);
    }
    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (canUseResidentGroupMap.has(group.id) && allowedGroupsMap.has(group.id)) {
        options.push({
          value: group.id,
          label: `${group.name}`
        });
      }
    }

    return options;
  }

  /**
   * ### 家族を取得する
   */
  public async getFamilies(): Promise<Array<FamilyGetModel>> {
    let response: Array<FamilyGetModel> = [];

    try {
      response = await this.familiesService.getsInFacility(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        const count: number = await this.familiesService.getCountInFacility(userStore);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount
          };
          response = response.concat(await this.familiesService.getsInFacility(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 家族MAPを作成する
   *
   * - key：利用者ID + "-" + 家族ID
   * - value：利用者グループ情報
   */
  public createFamilyMap(families: Array<FamilyGetModel>): Map<string, FamilyGetModel> {
    let map: Map<string, FamilyGetModel> = new Map<string, FamilyGetModel>();

    for (const family of families) {
      map.set(family.residentsSortKey, family);
    }

    return map;
  }

  /**
   * ### 家族セレクトボックス用項目を作成する
   *
   * - 渡された利用者IDを持つ家族のみを項目にする
   * - 渡された利用者IDが0(全て)の場合は全家族を項目にする
   * - 選択肢の表示項目名は「全て」と「家族名」
   */
  public createFamilyOptions(families: Array<FamilyGetModel>, residentIds: number[]): Array<{ value: string; label: string }> {
    let options: Array<{ value: string; label: string }> = [];
    for (const residentId of residentIds) {
      for (const family of families) {
        if ((family.id == residentId || residentId == 0) && family.familyLineId != undefined) {
          options.push({
            value: family.residentsSortKey,
            label: `${family.familyName}（${family.familyRelation}）`
          });
        }
      }
    }

    return options;
  }

  /**
   * ### 返信元のイベント取得
   */
  public async getEvent(eventId: number): Promise<EventGetModel> {
    const response: EventGetModel = await this.eventsService.get(userStore, eventId);
    return response;
  }

  /**
   * ### 参加方法の選択肢を作成する
   *
   * - 現地開催イベントは、「現地参加」のみ選択可能
   * - オンライン開催イベントは、「オンライン参加」のみ選択可能
   * - 現地とオンライン開催イベントは、「現地参加」と「オンライン参加」を選択可能
   */
  public createAttendanceTypeOptions(holdType: EventHoldType): Array<{ value: AttendType; text: string }> {
    switch (holdType) {
      case EventHoldType.LOCAL_ONLY: {
        return [{ value: AttendType.LOCAL, text: "現地参加" }];
      }
      case EventHoldType.ONLINE_ONLY: {
        return [{ value: AttendType.ONLINE, text: "オンライン参加" }];
      }
      case EventHoldType.LOCAL_AND_ONLINE: {
        return [
          { value: AttendType.LOCAL, text: "現地参加" },
          { value: AttendType.ONLINE, text: "オンライン参加" }
        ];
      }
      default: {
        return [];
      }
    }
  }

  /**
   * ### 削除ボタン押下
   */
  public onAttendeeDelete(index: number): void {
    if (1 < this.attendees.length) {
      this.attendees.splice(index, 1);
    } else {
      this.attendees.splice(index, 1);
      this.attendees.push({ name: "", attendanceType: undefined });
    }

    return;
  }

  /**
   * ### 参加者追加ボタン押下
   */
  public onAddAttendee(): void {
    this.attendees.push({
      name: "",
      attendanceType: undefined
    });

    return;
  }

  /**
   * ### 更新確認モーダルを表示する
   */
  public async showConfirmRegisterModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showRegisterModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### イベント返信を更新する
   */
  public async registerEventReply(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    const parentEventsSortKey: string = this.$route.params.id;
    const family: FamilyGetModel | undefined = this.familyMap.get(this.residentsSortKey);
    if (family != undefined) {
      const requestBody: EventRepliesPostModel = this.createRequestBody(parentEventsSortKey, family);
      await this.registerEventReplies(requestBody);

      // イベント一覧を再読み込みさせるようstoreを更新
      let eventList: EventListState = needRefreshesStore.eventList;
      eventList.eventList = true;
      needRefreshesStore.setEventList(eventList);

      // 登録成功ダイアログを表示する
      this.showRegisterSuccessModal = true;
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### イベント返信のリクエストボディを作成する
   */
  public createRequestBody(parentEventsSortKey: string, family: FamilyGetModel): EventRepliesPostModel {
    const requestBody: EventRepliesPostModel = {
      /** ソートキー。(parentEventsSortKey)_(residentId)_(familyId) */
      parentEventsSortKey: Number(parentEventsSortKey),
      /** 利用者ID */
      residentId: family.id,
      /** 家族ID */
      familyId: family.familyId,
      /** 家族のLINE ID */
      familyLineId: family.familyLineId as string,
      /** 参加者情報 */
      attendees: this.attendees as Array<Attendees>
    };

    return requestBody;
  }

  /**
   * ### イベント返信を登録する
   */
  public async registerEventReplies(requestBody: EventRepliesPostModel): Promise<void> {
    await this.eventRepliesService.post(userStore, requestBody);
    return;
  }

  /**
   * ### イベント一覧に遷移する
   */
  public gotoEventList(): void {
    this.$router.push(PcUrl.EventList);
    return;
  }
}
</script>

<style lang="scss" scoped>
#event-reply-edit {
  max-width: 530px;
  margin-left: auto;
  margin-right: auto;

  .attendees {
    margin-left: 20px;
    width: 468px;
    max-width: 468px;

    .attendees-title {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 4px;
    }

    .attendees-note {
      margin-bottom: 4px;
    }

    .attendee-type-select {
      width: 154px;
    }

    .attendee-delete-btn,
    .add-attendee-btn {
      width: 60px;
      font-size: 16px !important;
      height: 38px !important;
    }
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
